import { useDispatch } from "react-redux";
/**
 * 素材AI画像生成 hook（自由テキスト入力）
 */
import { useState, useCallback, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { recordEvent } from "app/analytics";
import { Asset, AnalyticsEventType } from "app/types";
import { AssetsResponse } from "features/user/api";
import { updateAiImageUsage, updateAssetUsage } from "features/subscription";
import { generateMaterialImageSSE } from "../api";
import { GenerationStatus, AIGenerateMaterialOptions, AIImageResult } from "../types";

interface UseAIMaterialImageGenerationProps {
  /** 対象ユーザーUID */
  uid: string;
  /** キャッシュ更新用クエリキー（デフォルト: ["creator/getUserAssets", uid]） */
  queryKey?: string;
}

interface UseAIMaterialImageGenerationReturn {
  status: GenerationStatus;
  progress: number;
  progresses: Map<number, number>;
  partialImage: string | null;
  partialImages: Map<number, string>;
  results: AIImageResult[];
  errorMessage: string | null;
  generate: (options: AIGenerateMaterialOptions) => Promise<void>;
  cancel: () => void;
  reset: () => void;
}

export const useAIMaterialImageGeneration = ({
  uid,
  queryKey = "creator/getUserAssets",
}: UseAIMaterialImageGenerationProps): UseAIMaterialImageGenerationReturn => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<GenerationStatus>(GenerationStatus.IDLE);
  const [progress, setProgress] = useState(0);
  const [progresses, setProgresses] = useState<Map<number, number>>(new Map());
  const [partialImage, setPartialImage] = useState<string | null>(null);
  const [partialImages, setPartialImages] = useState<Map<number, string>>(new Map());
  const [results, setResults] = useState<AIImageResult[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const generate = useCallback(
    async (options: AIGenerateMaterialOptions) => {
      if (!uid) {
        setStatus(GenerationStatus.ERROR);
        setErrorMessage("User not found");
        console.error("[AI Material Generation Error] User UID not found");
        return;
      }

      abortControllerRef.current = new AbortController();
      setStatus(GenerationStatus.GENERATING);
      setProgress(0);
      setProgresses(new Map());
      setPartialImage(null);
      setPartialImages(new Map());
      setErrorMessage(null);

      // 今回の生成で蓄積する結果
      const sessionResults: AIImageResult[] = [];

      try {
        await generateMaterialImageSSE({
          uid,
          options,
          signal: abortControllerRef.current.signal,
          callbacks: {
            onStart: () => {
              setProgress(0);
            },
            onPartialImage: (
              b64Json: string,
              prog: number,
              _index: number,
              imageIndex?: number,
            ) => {
              const idx = imageIndex ?? 0;

              // 各画像の進捗を更新
              setProgresses((prev) => {
                const next = new Map(prev);
                next.set(idx, prog);
                return next;
              });

              // 各画像の部分画像を更新
              const dataUrl = `data:image/webp;base64,${b64Json}`;
              setPartialImages((prev) => {
                const next = new Map(prev);
                next.set(idx, dataUrl);
                return next;
              });

              // 後方互換性: 単一progress/partialImageも更新（最新の値）
              setProgress(prog);
              setPartialImage(dataUrl);
            },
            onComplete: (imageUrl: string, id: number, _fileSize: number, _imageIndex?: number) => {
              // 個別画像完了: 結果を蓄積（statusはまだGENERATINGのまま）
              const newResult = { id, url: imageUrl, fileSize: 0 };
              sessionResults.push(newResult);
            },
            onAllComplete: (assets, usage) => {
              recordEvent(AnalyticsEventType.AI_IMAGE_GENERATE, {
                from: "material_image",
                result: "success",
              });
              // 全画像完了
              setStatus(GenerationStatus.COMPLETE);
              setProgress(100);
              setPartialImage(null);

              // APIから返された完全なAssetオブジェクトから結果を構築
              const finalResults: AIImageResult[] = assets.map((asset) => ({
                id: asset.id,
                url: asset.url,
                fileSize: asset.file_size,
              }));
              setResults((prev) => [...finalResults, ...prev]);

              // AI画像生成使用量を更新
              if (usage) {
                dispatch(updateAiImageUsage({ uid, used: usage.used, remaining: usage.remaining }));
              }

              // TanStack Queryキャッシュを更新（APIから返された完全なAssetオブジェクトをそのまま使用）
              const totalFileSize = assets.reduce((sum, a) => sum + (a.file_size || 0), 0);
              queryClient.setQueryData<AssetsResponse>([queryKey, uid], (old) => ({
                assets: [...(assets as Asset[]), ...(old?.assets || [])],
                totalSize: (old?.totalSize || 0) + totalFileSize,
              }));

              // Asset使用量（容量）を更新
              dispatch(updateAssetUsage({ uid, delta: totalFileSize }));
            },
            onError: (code: string, message: string) => {
              recordEvent(AnalyticsEventType.AI_IMAGE_GENERATE, {
                from: "material_image",
                result: "error",
                error_code: code,
              });
              setStatus(GenerationStatus.ERROR);
              setErrorMessage(message);
              console.error("[AI Material Generation Error]", code, message);
            },
          },
        });
      } catch (error) {
        if ((error as Error).name === "AbortError") {
          setStatus(GenerationStatus.IDLE);
          setProgress(0);
          setProgresses(new Map());
          setPartialImage(null);
          setPartialImages(new Map());
        } else {
          setStatus(GenerationStatus.ERROR);
          const errorMsg = (error as Error).message || "Unknown error";
          setErrorMessage(errorMsg);
          console.error("[AI Material Generation Error]", error);
        }
      }
    },
    [uid, dispatch, queryClient, queryKey],
  );

  const cancel = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setStatus(GenerationStatus.IDLE);
    setProgress(0);
    setProgresses(new Map());
    setPartialImage(null);
    setPartialImages(new Map());
  }, []);

  const reset = useCallback(() => {
    cancel();
    setResults([]);
    setErrorMessage(null);
  }, [cancel]);

  return {
    status,
    progress,
    progresses,
    partialImage,
    partialImages,
    results,
    errorMessage,
    generate,
    cancel,
    reset,
  };
};
